/**
 * Builds the county-search filter parameters the gardens endpoint
 * understands when a Google place_id is selected.
 *
 * The county-search contract on `GET /api/gardens` accepts (in addition to
 * `filter[geo_bounding_box]`):
 *
 *   filter[place_id]         - Google place_id; primary cache key for the
 *                              cached OSM polygon.
 *   filter[place_name]       - short county name; fallback cache key on
 *                              the first request (cached rows from
 *                              `polygons:warm` are populated without a
 *                              Google place_id until the first county
 *                              search backfills one).
 *   filter[radius_miles]     - user-selected radius in miles.
 *   filter[expanded_polygon] - the SAME simplified ring the visual overlay
 *                              draws (produced once by bufferRing), here just
 *                              rounded to 5dp (~1 m) for the wire, so the ES
 *                              filter matches the overlay to ~1 m.
 *                              Only sent in polygon mode (0 < r <= 5)
 *                              once the FE has the base polygon cached.
 *
 * Centralising this logic here keeps `store/modules/garden.js` focused on
 * coordination, and makes the FE side of the county-search contract easy
 * to find from one place.
 *
 * @see web/docs/FILTERS.md#county-polygon-search
 * @see api/docs/api/search-area-resolver.md
 */
import { bufferRing, pathsToLngLatRing } from '@/services/geo/polygonBuffer'
import { modeForRadius } from '@/services/geo/searchAreaMode'
import { detectLocationType } from '@/utils/locationType'

const COUNTY_PLACE_NAME_COMPONENT_TYPES = [
  'administrative_area_level_2',
  'locality',
  'colloquial_area',
  'natural_feature',
]

/**
 * Pick the long_name from the first address_component that matches one of
 * the county-shaped Google place types. Returns null if no component
 * matches or the location has no address_components.
 */
function extractPlaceName(location) {
  const components = location?.address_components
  if (!Array.isArray(components)) return null
  const match = components.find((c) => Array.isArray(c?.types)
    && c.types.some((t) => COUNTY_PLACE_NAME_COMPONENT_TYPES.includes(t)))
  return match?.long_name ?? null
}

/**
 * Build the FE-buffered ring at polygon-mode radii (1mi, 5mi) and serialise
 * it for the API. Returns null when the FE doesn't yet have the base
 * polygon (first request) or the radius is outside polygon mode.
 *
 * @param {Array<{lat:number,lng:number}>|null} basePaths
 * @param {number} radiusMiles
 * @returns {string|null}
 */
function serialiseExpandedPolygon(basePaths, radiusMiles) {
  if (!basePaths || modeForRadius(radiusMiles) !== 'polygon') return null
  const expandedPaths = bufferRing(basePaths, radiusMiles)
  const ring = pathsToLngLatRing(expandedPaths)
  if (!ring) return null
  return JSON.stringify(ring)
}

/**
 * Build the county-search filter params for a selected location and
 * radius. Returns an empty object when the inputs don't constitute a
 * county search (no place_id, no radius, garden-name search, etc.) so
 * the caller can spread the result unconditionally.
 *
 * @param {object} params
 * @param {object|null} params.selectedLocation     Vuex `location/selectedLocation`.
 * @param {{value:number}|null} params.selectedRadius Vuex `location/selectedRadius`.
 * @param {Array<{lat:number,lng:number}>|null} params.basePaths
 *   Cached county outline from `map/searchAreaBasePaths`; supplies the
 *   geometry to buffer for `filter[expanded_polygon]`.
 * @param {string|null} params.searchAreaPlaceId
 *   `map/searchAreaPlaceId`: the place the cached outline belongs to. On a
 *   county switch `basePaths` still holds the previous county at
 *   request-build time, which made every 2nd+ search return the previous
 *   county's gardens (UAT, 2026-06-10).
 * @param {boolean} params.isGardenSearch When true, returns {} (garden-name
 *   searches are not location-constrained).
 * @returns {Record<string, string|number>}
 */
// eslint-disable-next-line import/prefer-default-export
export function buildCountyFilterParams({
  selectedLocation,
  selectedRadius,
  basePaths,
  searchAreaPlaceId,
  isGardenSearch,
}) {
  if (isGardenSearch) return {}

  // SoW: only counties get the county-search params. A postcode geocode result
  // also carries a place_id and a radius, and its address_components name the
  // enclosing county, so gating on place_id + radius alone wrongly promoted
  // postcodes (e.g. SW1A 1AA -> Greater London, TN15 0RR -> Kent) to a county
  // polygon. Gate on the location actually being a county.
  //
  // detectLocationType keys on the TOP-LEVEL geocode types (a postcode is
  // 'postal_code' there, never 'county'), preserves the London-as-county
  // override, and classifies Kent/Surrey (administrative_area_level_2),
  // Cumbria/Bedfordshire (colloquial_area) and Isle of Wight (natural_feature)
  // as counties. We deliberately do NOT fall back to a server-supplied
  // location_type here: this is a scope gate, and gating purely on the
  // deterministic top-level-types classifier keeps it immune to any future
  // divergence in the API's enrichGeocodeResults. detectLocationType(null)
  // returns null, so a drag that clears selectedLocation yields {} (the D2
  // emission gate) without any drag/lock awareness here.
  if (detectLocationType(selectedLocation) !== 'county') return {}

  const placeId = selectedLocation?.place_id
  const radiusMiles = selectedRadius?.value
  if (!placeId || radiusMiles === undefined || radiusMiles === null) return {}

  const params = {
    'filter[place_id]': placeId,
    'filter[radius_miles]': radiusMiles,
  }

  const placeName = extractPlaceName(selectedLocation)
  if (placeName) {
    params['filter[place_name]'] = placeName
  }

  // Only send the cached outline when it belongs to the place being
  // searched; a stale ring from the previous county misfilters the results.
  if (searchAreaPlaceId && searchAreaPlaceId === placeId) {
    const expandedPolygon = serialiseExpandedPolygon(basePaths, radiusMiles)
    if (expandedPolygon) {
      params['filter[expanded_polygon]'] = expandedPolygon
    }
  }

  return params
}
